import { useMemo, useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { logger } from '../utils/logger';
import { isDemoMode } from '../utils/demoUtils';
import { apiClient } from '../services/api/secureApiClient';

const IS_LOCALSTACK = process.env.REACT_APP_USE_LOCALSTACK === 'true';
const AUTH0_CONFIGURED = !!process.env.REACT_APP_AUTH0_DOMAIN;

export const useAuth = () => {
  const auth0 = useAuth0();
  const [backendPermissions, setBackendPermissions] = useState(null);
  const [backendAssignedName, setBackendAssignedName] = useState(null);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const fetchedForUser = useRef(null);

  const {
    user,
    isAuthenticated = false,
    isLoading = false,
    getAccessTokenSilently = async () => null,
    loginWithRedirect = () => logger.warn('Auth0 login not available'),
    logout = () => logger.warn('Auth0 logout not available'),
  } = auth0 || {};

  // Fetch permissions from backend when Auth0 user is authenticated
  useEffect(() => {
    if (!AUTH0_CONFIGURED || !isAuthenticated || !user?.sub) return;
    if (fetchedForUser.current === user.sub) return;

    fetchedForUser.current = user.sub; // Set before fetch to prevent duplicate requests

    let cancelled = false;
    const fetchPermissions = async () => {
      setPermissionsLoading(true);
      try {
        apiClient.setTokenGetter(getAccessTokenSilently);
        const response = await apiClient.makeRequest('/players/me', { requireAuth: true });
        if (!cancelled) {
          setBackendPermissions(response.permissions || ['read:matches']);
          setBackendAssignedName(response.assignedName || null);
        }
      } catch (error) {
        logger.error('Failed to fetch permissions from backend:', error);
        if (!cancelled) {
          fetchedForUser.current = null; // Reset on failure so retry is possible
          setBackendPermissions(['read:matches']);
          setBackendAssignedName(null);
        }
      } finally {
        if (!cancelled) setPermissionsLoading(false);
      }
    };

    fetchPermissions();
    return () => { cancelled = true; };
  }, [isAuthenticated, user?.sub, getAccessTokenSilently]);

  // Mock auth for LocalStack development
  const localStackUser = useMemo(
    () => ({
      email: 'john.wick@office-arena.local',
      name: 'John Wick',
      sub: 'localstack-user-123',
      permissions: ['read:matches', 'write:matches', 'write:bread'],
    }),
    []
  );

  // Extract permissions from backend response or fallback
  const { permissions, assignedName } = useMemo(() => {
    // Priority 1: Demo Mode (Force Admin/John Wick)
    if (isDemoMode()) {
      return {
        permissions: ['read:matches', 'write:matches', 'write:bread'],
        assignedName: 'John Wick'
      };
    }

    // Priority 2: Real Auth0 - use backend permissions
    if (AUTH0_CONFIGURED) {
      if (!isAuthenticated || !user) {
        return { permissions: ['read:matches'], assignedName: null };
      }
      // Use backend-fetched permissions (default to read-only while loading)
      return {
        permissions: backendPermissions || ['read:matches'],
        assignedName: backendAssignedName,
      };
    }

    // Priority 3: LocalStack Mock
    if (IS_LOCALSTACK) {
      return {
        permissions: localStackUser.permissions,
        assignedName: 'John Wick'
      };
    }

    // Default public read
    return { permissions: ['read:matches'], assignedName: null };
  }, [isAuthenticated, user, localStackUser.permissions, backendPermissions, backendAssignedName]);

  // Permission checks
  const hasPermission = permission => permissions.includes(permission);
  const isAdmin = hasPermission('write:bread');
  const isPlayer = hasPermission('write:matches');
  const canWriteMatches = hasPermission('write:matches');
  const canWriteBread = hasPermission('write:bread');

  // Return Demo Mode User (auto-login only in demo mode)
  if (isDemoMode()) {
    logger.info('Demo Mode: Auto-login as John Wick');
    return {
      user: localStackUser,
      isAuthenticated: true,
      isLoading: false,
      getAccessTokenSilently: async () => 'demo-token',
      loginWithRedirect: () => logger.debug('Login disabled in Demo Mode'),
      logout: () => logger.debug('Logout disabled in Demo Mode'),
      permissions,
      assignedName,
      hasPermission,
      isAdmin,
      isPlayer,
      canWriteMatches,
      canWriteBread,
    };
  }

  // Priority: Real Auth0 user (even in LocalStack dev mode)
  if (AUTH0_CONFIGURED && isAuthenticated && user && user.sub) {
    logger.info('Auth0: User authenticated', {
      userId: user.sub,
      email: user.email,
      isAdmin,
      canWriteMatches,
    });
    return {
      user,
      isAuthenticated,
      isLoading: isLoading || permissionsLoading,
      getAccessTokenSilently,
      loginWithRedirect,
      logout,
      permissions,
      assignedName,
      hasPermission,
      isAdmin,
      isPlayer,
      canWriteMatches,
      canWriteBread,
    };
  }

  // Auth0 configured but not authenticated - provide login function
  if (AUTH0_CONFIGURED) {
    logger.info('Auth0: Not authenticated, showing login option');
    return {
      user: null,
      isAuthenticated: false,
      isLoading,
      getAccessTokenSilently,
      loginWithRedirect,
      logout,
      permissions: ['read:matches'],
      assignedName: null,
      hasPermission,
      isAdmin: false,
      isPlayer: false,
      canWriteMatches: false,
      canWriteBread: false,
    };
  }

  // Fallback: LocalStack without Auth0 (pure development mode)
  if (IS_LOCALSTACK) {
    logger.info('LocalStack: No Auth0 configured, public access mode');
    return {
      user: null,
      isAuthenticated: false,
      isLoading: false,
      getAccessTokenSilently: async () => null,
      loginWithRedirect: () => logger.warn('No authentication configured'),
      logout: () => logger.warn('No authentication configured'),
      permissions: ['read:matches'],
      assignedName: null,
      hasPermission,
      isAdmin: false,
      isPlayer: false,
      canWriteMatches: false,
      canWriteBread: false,
    };
  }

  return {
    user,
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    loginWithRedirect,
    logout,
    permissions,
    assignedName,
    hasPermission,
    isAdmin,
    isPlayer,
    canWriteMatches,
    canWriteBread,
  };
};
